import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Save, ThumbsDown } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

/** Superadmin control for the AI matching thresholds (suggestion floor + auto-confirm). */
export default function MatchSettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rej, setRej] = useState(null);

  const load = () => api("/admin/match-settings").then(setS).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  useEffect(() => { api("/admin/match-rejections/summary").then(setRej).catch(() => setRej({ total: 0, reasons: [], top_categories: [] })); }, []);

  async function save() {
    setSaving(true);
    try {
      const res = await api("/admin/match-settings", {
        method: "PATCH",
        body: { min_score: Number(s.min_score), auto_match_score: Number(s.auto_match_score) },
      });
      setS(res);
      toast.success("Seuils de correspondance enregistrés");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  }

  if (!s) return <div className="h-40 rounded-2xl bg-slate-100 animate-pulse" data-testid="match-settings-loading" />;

  const rows = [
    {
      key: "min_score",
      title: "Seuil de suggestion",
      desc: "Score minimum (0–100 %) pour qu'un objet soit proposé au client comme correspondance possible. Plus haut = suggestions plus strictes.",
    },
    {
      key: "auto_match_score",
      title: "Seuil d'auto-validation",
      desc: "Score minimum pour qu'une correspondance soit validée automatiquement (sans intervention). Toujours ≥ seuil de suggestion.",
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-6 max-w-2xl" data-testid="match-settings-panel">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><Sparkles size={18} /></div>
        <h3 className="font-display font-bold text-lg text-slate-900">Correspondance intelligente (IA)</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">Ajustez la sévérité de l'algorithme de rapprochement entre les déclarations B2C et les objets en dépôt B2B.</p>

      <div className="space-y-7">
        {rows.map((r) => {
          const pct = Math.round((Number(s[r.key]) || 0) * 100);
          return (
            <div key={r.key}>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-800">{r.title}</label>
                <span className="text-sm font-bold px-2 py-0.5 rounded bg-slate-100 text-findo-indigo" data-testid={`match-${r.key}-value`}>{pct}%</span>
              </div>
              <input
                type="range" min="0" max="100" step="5" value={pct}
                onChange={(e) => setS({ ...s, [r.key]: Number(e.target.value) / 100 })}
                className="mt-2 w-full accent-findo-indigo"
                data-testid={`match-${r.key}-slider`}
              />
              <p className="text-xs text-slate-400 mt-1">{r.desc}</p>
            </div>
          );
        })}
      </div>

      <Button onClick={save} disabled={saving} className="mt-7 rounded-xl bg-findo-navy hover:bg-findo-navy/90 font-semibold" data-testid="match-settings-save">
        <Save size={15} className="mr-1.5" /> {saving ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Enregistrement…</span> : "Enregistrer"}
      </Button>

      <div className="mt-8 pt-6 border-t border-border" data-testid="match-rejections-recap">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="h-9 w-9 grid place-items-center rounded-xl bg-findo-red/10 text-findo-red"><ThumbsDown size={17} /></div>
          <h3 className="font-display font-bold text-base text-slate-900">Motifs de rejet des correspondances</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Ce que les propriétaires indiquent quand ils rejettent un objet proposé — utile pour prioriser l'amélioration du matching.</p>
        {!rej ? (
          <div className="h-16 rounded-xl bg-slate-100 animate-pulse" />
        ) : rej.total === 0 ? (
          <p className="text-sm text-slate-400" data-testid="match-rejections-empty">Aucun rejet enregistré pour l'instant.</p>
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-3">{rej.total} rejet(s) au total</p>
            <div className="space-y-2">
              {rej.reasons.map((r) => {
                const pct = Math.round((r.count / rej.total) * 100);
                return (
                  <div key={r.reason} data-testid={`rejection-reason-${r.reason}`}>
                    <div className="flex items-center justify-between text-sm mb-0.5">
                      <span className="text-slate-700 font-medium">{r.reason}</span>
                      <span className="text-slate-400">{r.count} · {pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-findo-red/70 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {rej.top_categories?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="match-rejections-categories">
                <span className="text-xs text-slate-400 mr-1 self-center">Catégories les plus rejetées :</span>
                {rej.top_categories.map((c) => (
                  <span key={c.category} className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{c.category} ({c.count})</span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
